import React, { useEffect, useState } from 'react';
import { api, AED_short, getWorkspace } from '../../lib/api';
import MobileShell from '../../components/MobileShell';
import {
  BarChart3, Flame, Sparkles, Home, MapPin, Loader2, RefreshCcw, ChevronRight, TrendingUp, TrendingDown
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { toast } from 'sonner';

export default function MobileTrends() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [hotspots, setHotspots] = useState(null);
  const [hotListings, setHotListings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    const fromDate = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    try {
      const [s, t, c] = await Promise.all([
        api.get(`/trends/summary?tx_type=sale&from_date=${fromDate}`),
        api.get(`/trends/timeseries?days=${days}`),
        api.get(`/trends/communities?top=8`),
      ]);
      setSummary(s.data); setSeries(t.data); setCommunities(c.data);
    } finally { setLoading(false); }
    // AI runs separately (cached, non-blocking)
    api.get('/trends/ai-hotspots').then(r => setHotspots(r.data)).catch(() => {});
    api.get('/trends/ai-hot-listings').then(r => setHotListings(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const seed = async () => {
    try {
      await api.post('/trends/seed');
      toast.success('Sample data loaded');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const isEmpty = summary && summary.count === 0;

  return (
    <MobileShell title="Trends" noHeader>
      <div className="pt-3 pb-2 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center shadow-md">
          <BarChart3 className="text-white" size={18}/>
        </div>
        <div className="flex-1">
          <h1 className="font-serif text-3xl leading-none">Trends</h1>
          <div className="text-[11px] text-slate-500 mt-1">Dubai DLD · Iris AI market intel</div>
        </div>
      </div>

      {loading && !summary && (
        <div className="py-16 text-center text-slate-400 text-sm">
          <Loader2 size={16} className="inline animate-spin mr-2"/>Loading market data…
        </div>
      )}

      {isEmpty && getWorkspace() === 'live' && (
        <div className="glass p-6 text-center my-4" data-testid="m-trends-empty-live">
          <BarChart3 size={28} className="mx-auto mb-2 text-slate-300"/>
          <div className="font-serif text-lg">No market data yet</div>
          <div className="text-xs text-slate-500 mt-1 leading-relaxed">
            Real DLD transactions arrive via the daily 06:00 Dubai sync. Switch to <span className="font-medium">Demo</span> to explore with sample data.
          </div>
        </div>
      )}

      {isEmpty && getWorkspace() !== 'live' && (
        <div className="glass p-6 text-center my-4" data-testid="m-trends-empty">
          <BarChart3 size={28} className="mx-auto mb-2 text-slate-300"/>
          <div className="font-serif text-lg">No market data</div>
          <div className="text-xs text-slate-500 mt-1 mb-4">Load 500 sample Dubai transactions to explore.</div>
          <button onClick={seed} data-testid="m-trends-seed" className="btn-primary text-sm">Seed sample data</button>
        </div>
      )}

      {!isEmpty && summary && (
        <>
          {/* KPI pills — swipe row */}
          <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 mb-3 scrollbar-none" data-testid="m-trends-kpis">
            <MKpi label="Deals" value={summary.count.toLocaleString()} accent="text-slate-800"/>
            <MKpi label="Total value" value={AED_short(summary.total_value_aed)} accent="text-[#679286]"/>
            <MKpi label="Avg /sqft" value={Math.round(summary.avg_price_per_sqft).toLocaleString()}/>
            <MKpi label="High" value={Math.round(summary.highest_deal?.aed_per_sqft || 0).toLocaleString()} sub={summary.highest_deal?.community?.slice(0, 12)} accent="text-orange-500"/>
            <MKpi label="Low" value={Math.round(summary.lowest_deal?.aed_per_sqft || 0).toLocaleString()} sub={summary.lowest_deal?.community?.slice(0, 12)} accent="text-blue-600"/>
          </div>

          {/* Range toggle */}
          <div className="flex gap-1 bg-white/70 rounded-full p-0.5 mb-4 border border-white/60">
            {[7, 30, 90, 365].map(d => (
              <button key={d} onClick={() => setDays(d)}
                data-testid={`m-trends-range-${d}`}
                className={`flex-1 py-1.5 rounded-full text-[11px] font-semibold ${days === d ? 'bg-[#1A2530] text-white' : 'text-slate-600'}`}>
                {d === 7 ? '7d' : d === 30 ? '30d' : d === 90 ? '90d' : '1y'}
              </button>
            ))}
          </div>

          {/* AI Hotspots */}
          <div className="relative rounded-2xl overflow-hidden mb-4 border border-white/60 shadow-md" data-testid="m-ai-hotspots">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1A2530] via-[#243544] to-[#0F1720]"/>
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-orange-500/20 blur-3xl"/>
            <div className="relative p-4">
              <div className="flex items-center gap-2 mb-2">
                <Flame size={16} className="text-orange-300"/>
                <div className="font-serif text-lg text-white">Iris AI Hotspots</div>
              </div>
              {!hotspots ? (
                <div className="py-4 text-center text-slate-300 text-xs"><Loader2 size={12} className="inline animate-spin mr-1"/>Analyzing…</div>
              ) : (
                <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 scrollbar-none">
                  {(hotspots.hotspots || []).slice(0, 5).map((h, i) => (
                    <div key={i} data-testid={`m-hotspot-${i}`} className="shrink-0 w-52 rounded-xl bg-white/5 border border-white/10 p-3 backdrop-blur-sm">
                      <div className={`font-serif text-2xl ${h.score >= 85 ? 'text-orange-300' : 'text-amber-300'}`}>
                        {h.score}<span className="text-[10px] opacity-60">/100</span>
                      </div>
                      <div className="text-sm font-medium text-white line-clamp-1 mt-1">{h.community}</div>
                      <div className="text-[10px] text-slate-300 line-clamp-2 mt-1 min-h-[2rem]">{h.headline}</div>
                      {h.suggested_ticket_aed && (
                        <div className="text-[10px] text-orange-200 mt-2 pt-1.5 border-t border-white/10">
                          {AED_short(h.suggested_ticket_aed)} · <span className="capitalize">{h.suggested_property_type}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* AED/sqft chart */}
          <div className="glass p-4 mb-4" data-testid="m-chart-timeseries">
            <div className="overline mb-2">AED/sqft last {days}d</div>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
                <XAxis dataKey="date" fontSize={9} tickFormatter={d => d?.slice(5)}/>
                <YAxis fontSize={9}/>
                <Tooltip/>
                <Line type="monotone" dataKey="avg_price_per_sqft" stroke="#679286" strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* AI Hot Listings */}
          <div className="glass p-4 mb-4" data-testid="m-ai-hot-listings">
            <div className="flex items-center gap-2 mb-2">
              <Home size={14} className="text-[#679286]"/>
              <div className="font-serif text-lg">Iris AI Hot Listings</div>
            </div>
            {!hotListings ? (
              <div className="py-4 text-center text-slate-400 text-xs"><Loader2 size={12} className="inline animate-spin mr-1"/>Scoring…</div>
            ) : (
              <div className="space-y-2">
                {(hotListings.hot_listings || []).slice(0, 5).map((h, i) => (
                  <div key={i} data-testid={`m-hot-listing-${i}`} className="rounded-xl bg-white/70 border border-white/60 p-3">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <div className="text-sm font-medium text-slate-800 line-clamp-1">{h.building}</div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-1"><MapPin size={9}/>{h.community}</div>
                      </div>
                      <div className={`font-serif text-xl ${(h.hotness_score || 0) >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {h.hotness_score}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-700 line-clamp-2 mb-2">{h.reasoning}</div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-semibold text-[#679286]">{AED_short(h.asking_price_aed)}</span>
                      {h.discount_vs_sold_pct != null && (
                        <span className={`font-semibold ${h.discount_vs_sold_pct < 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {h.discount_vs_sold_pct > 0 ? '+' : ''}{h.discount_vs_sold_pct}% vs sold
                        </span>
                      )}
                      <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-slate-900/85 text-white capitalize">{(h.call_to_action || 'watch').replace('_', ' ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Community leaderboard — top 8 */}
          <div className="glass p-4 mb-4" data-testid="m-community-leaderboard">
            <div className="overline mb-3">Price/sqft by area</div>
            <div className="space-y-1.5">
              {communities.slice(0, 10).map((r, i) => (
                <div key={r.community} data-testid={`m-community-${i}`} className="flex items-center gap-2 py-1.5 border-b border-slate-100/60 last:border-b-0">
                  <div className="text-[10px] text-slate-400 w-5">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{r.community}</div>
                    <div className="text-[10px] text-slate-500">{r.count} deals</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">{Math.round(r.avg_price_per_sqft || 0).toLocaleString()}</div>
                    <div className="text-[9px] text-slate-500">AED/sqft</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </MobileShell>
  );
}

function MKpi({ label, value, sub, accent = 'text-slate-800' }) {
  return (
    <div className="shrink-0 min-w-[110px] rounded-2xl bg-white/85 border border-white/60 px-3 py-2.5">
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-serif text-lg ${accent} leading-tight mt-0.5`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 truncate max-w-[100px]">{sub}</div>}
    </div>
  );
}
